import React, { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FileText, History } from 'lucide-react';
import Button from '../../../components/Button';
import StatCard from '../../../components/StatCard';
import Chip from '../../../components/Chip';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import AdminTabs from '../../components/AdminTabs';
import { HelpTrigger } from '../../../help';
import { OpenExternalIcon, RefreshIcon, SuccessIcon, WarningIcon } from '../../../components/icons';
import { formatDateTime } from '../../../utils/dateUtils';
import {
  fetchDocumentCoverage,
  fetchOperationsSummary,
  fetchRunDetail,
  fetchRunSummaries,
  fetchSourceChanges,
  type DocumentCoverageEntry,
  type ImportRunSummary,
  type SourceChangeEntry,
} from '../../../components/ballot-initiatives/operationsApi';

export type OperationsView = 'initiatives' | 'runs' | 'changes' | 'documents';

interface BallotInitiativeOperationsProps {
  activeView: OperationsView;
  cycle: string;
  onViewChange: (view: OperationsView) => void;
  onRefresh: () => void;
  onAcquireOne: (initiative?: DocumentCoverageEntry) => void;
  onBulkPreview: () => void;
  /** Drill into the initiatives list filtered to "Needs publish" (PRD v2 §5.3.5). */
  onNeedsPublish?: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const VIEW_OPTIONS: Array<{ key: OperationsView; label: string }> = [
  { key: 'initiatives', label: 'Initiatives' },
  { key: 'runs', label: 'Runs & Failures' },
  { key: 'changes', label: 'Source Changes' },
  { key: 'documents', label: 'Document Coverage' },
];

const sourceLabel = (source: string): string => source.includes('sos')
  ? 'Secretary of State'
  : source.includes('legislative') ? 'Legislative Council' : source;

const operationLabel = (operation: string): string => ({
  index_import: 'Source index refresh',
  document_acquisition: 'Document acquisition',
  periodic_refresh: 'Periodic refresh',
}[operation] ?? operation.replace(/_/g, ' '));

const dispositionLabel = (disposition: string): string => ({
  applied: 'Applied to initiative',
  sos_precedence_suppressed: 'Not applied because SOS evidence takes precedence',
  human_owned_suppressed: 'Not applied because an administrator manages this field',
  observation_only: 'Retained as source evidence only',
}[disposition] ?? disposition.replace(/_/g, ' '));

const displayValue = (value: unknown): string => {
  if (value == null || value === '') return 'Not provided';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

const RUN_METRIC_LABELS: Record<string, string> = {
  records_parsed: 'Records parsed',
  initiatives_inserted: 'Initiatives added',
  identities_considered: 'Initiatives considered',
  identities_refreshed: 'HTML pages refreshed',
  material_changes: 'Material changes',
  unchanged_observations: 'Unchanged observations',
  documents_acquired: 'Documents acquired',
  documents_reused: 'Documents reused',
  pdfs_reused: 'PDFs reused',
  documents_failed: 'Document failures',
  observed_failures: 'Observed source failures',
  identities_remaining: 'Initiatives remaining',
  analysis_pages_acquired: 'Analysis pages acquired',
  analysis_drafts_acquired: 'Analysis drafts acquired',
};

const documentTypeLabel = (type: string): string => ({
  sos_result_page: 'SOS result page',
  sos_final_text: 'SOS final text',
  legislative_detail_page: 'Legislative Council detail page',
  legislative_fiscal: 'Legislative Council fiscal analysis',
  legislative_ballot_analysis_page: 'Ballot analysis detail page',
  legislative_ballot_analysis_draft: 'Ballot analysis draft PDF',
}[type] ?? type.replace(/_/g, ' '));

const documentStateLabel = (state: string, successfulArtifact: boolean): string => {
  if (successfulArtifact || state === 'reused') return 'Retained artifact available';
  return ({
    missing_link: 'No current official link',
    eligible: 'Ready to acquire',
    selected: 'Official link selected',
    ambiguous: 'Official links are ambiguous',
    blocked_redirect: 'Blocked delegated redirect',
    failed: 'Last acquisition failed',
    skipped: 'Not eligible for acquisition',
  }[state] ?? state.replace(/_/g, ' '));
};

const formatTime = (value: string | null): string => value ? formatDateTime(value) : 'Not recorded';

const statusVariant = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (status === 'completed' || status === 'healthy') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running' || status === 'partial') return 'warning';
  return 'neutral';
};

const sourceHealthLabel = (status: string): string => ({
  healthy: 'Healthy',
  failed: 'Needs attention',
  never_checked: 'Never checked',
}[status] ?? status.replace(/_/g, ' '));

const parseStateLabel = (state: string | null): string => ({
  parsed: 'Parsed successfully',
  failed: 'Parse failed',
  pending: 'Parsing pending',
  skipped: 'Parsing skipped',
}[state || ''] ?? (state ? state.replace(/_/g, ' ') : 'Parse state not recorded'));

const SummaryPanel: React.FC<{ cycle: string; onRefresh: () => void; onNeedsPublish?: () => void }> = ({ cycle, onRefresh, onNeedsPublish }) => {
  const query = useQuery({
    queryKey: ['ballot-initiative-operations-summary', cycle],
    queryFn: () => fetchOperationsSummary(cycle),
    refetchInterval: data => data?.runningOperation ? 3000 : false,
  });

  if (query.isLoading) {
    return <div className="card flex min-h-24 items-center justify-center"><LoadingSpinner size="sm" /></div>;
  }
  if (query.isError || !query.data) {
    return (
      <div className="card flex flex-wrap items-center gap-3 border-l-4 border-warning-500 p-4" role="alert">
        <WarningIcon className="h-5 w-5 text-warning-600" />
        <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
          Operations status could not be loaded. Initiative records remain available below.
        </p>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>Retry</Button>
      </div>
    );
  }

  const summary = query.data;
  return (
    <section className="card space-y-4 p-4" aria-label="Ballot initiative operations summary">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
            Current official cycle
          </p>
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {summary.currentCycle || cycle || 'Not configured'}
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Last successful live refresh: {formatTime(summary.lastSuccessfulLiveRun?.completedAt ?? null)}
          </p>
        </div>
        <Button variant="primary" size="sm" icon={RefreshIcon} className="min-h-11" onClick={onRefresh}>
          Refresh Current Official Data
        </Button>
      </div>
      <div className="hidden grid-cols-2 gap-3 sm:grid lg:grid-cols-5">
        <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Initiatives</p>
          <p className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">{summary.initiativeCount}</p>
        </div>
        {/* The one drill-down tile in this row: clicking it applies the "Needs
            publish" filter to the list below. The importer creates rows
            unpublished, so this is the number that says someone must click
            Publish. StatCard carries the warning tint and the click affordance;
            the sibling tiles predate the shared component. */}
        <StatCard
          label="Needs publish"
          value={summary.needsPublishCount}
          variant={summary.needsPublishCount > 0 ? 'warning' : 'default'}
          onClick={onNeedsPublish}
          sublabel="On the ballot, not yet published"
        />
        <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Changes in that live refresh</p>
          <p className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">{summary.materialChangeCount}</p>
        </div>
        <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Failures in that live refresh</p>
          <p className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">{summary.documentFailureCount}</p>
        </div>
        <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Current operation</p>
          <p className="truncate text-sm font-semibold text-text-primary dark:text-text-dark-primary">
            {summary.runningOperation ? operationLabel(summary.runningOperation.operation) : 'None'}
          </p>
        </div>
      </div>
      <details className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary sm:hidden">
        <summary className="min-h-11 cursor-pointer py-2 text-sm font-medium text-text-primary dark:text-text-dark-primary">
          More operation metrics
        </summary>
        <dl className="grid grid-cols-2 gap-3 pt-2 text-sm">
          <div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Initiatives</dt><dd className="font-semibold">{summary.initiativeCount}</dd></div>
          <div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Needs publish</dt><dd className="font-semibold">{summary.needsPublishCount}</dd></div>
          <div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Live changes</dt><dd className="font-semibold">{summary.materialChangeCount}</dd></div>
          <div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Unhealthy sources</dt><dd className="font-semibold">{summary.sourceFailureCount}</dd></div>
          <div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Document failures</dt><dd className="font-semibold">{summary.documentFailureCount}</dd></div>
        </dl>
      </details>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(summary.sourceHealth).map(([source, health]) => (
          <div key={source} className="rounded-lg border border-border-light p-3 dark:border-border-dark">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">{sourceLabel(source)}</p>
              <Chip label={sourceHealthLabel(health.status)} variant={statusVariant(health.status)} size="sm" />
            </div>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Checked {formatTime(health.checkedAt)} · {parseStateLabel(health.parseState)}
            </p>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Last successful check {formatTime(health.lastSuccessfulAt)}
            </p>
          </div>
        ))}
      </div>
      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Times are shown in your local timezone.</p>
    </section>
  );
};

const RunsView: React.FC = () => {
  const query = useInfiniteQuery(
    ['ballot-initiative-run-summaries'],
    ({ pageParam = undefined }) => fetchRunSummaries(pageParam),
    {
      getNextPageParam: page => page.nextCursor || undefined,
      refetchInterval: data => data?.pages.some(page => page.entries.some(run => run.status === 'running')) ? 3000 : false,
    }
  );
  const runs = query.data?.pages.flatMap(page => page.entries) ?? [];
  return (
    <section className="card p-4" aria-labelledby="initiative-runs-heading" aria-busy={query.isFetching}>
      <h3 id="initiative-runs-heading" className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Runs & Failures</h3>
      <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">Official-source operations, outcomes, and retained failures.</p>
      {query.isLoading ? <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        : query.isError ? <ErrorState label="Run history could not be loaded." retry={() => query.refetch()} />
          : runs.length === 0 ? <EmptyCopy icon={History} text="No ballot initiative operations have run yet." />
            : <div className="mt-4 space-y-3">{runs.map(run => <RunCard key={run.id} run={run} />)}</div>}
      {query.hasNextPage && <LoadMore loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()} />}
    </section>
  );
};

const RunCard: React.FC<{ run: ImportRunSummary }> = ({ run }) => {
  const [expanded, setExpanded] = useState(false);
  const detail = useQuery({
    queryKey: ['ballot-initiative-run-detail', run.id],
    queryFn: () => fetchRunDetail(run.id),
    enabled: expanded,
    staleTime: 30_000,
  });
  const metrics = Object.entries(RUN_METRIC_LABELS)
    .filter(([key]) => run.summary[key] != null)
    .map(([key, label]) => ({ key, label, value: run.summary[key] }));
  return (
  <details className="rounded-lg border border-border-light p-3 dark:border-border-dark" onToggle={(event: React.SyntheticEvent<HTMLDetailsElement>) => setExpanded(event.currentTarget.open)}>
    <summary className="cursor-pointer list-none">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium text-text-primary dark:text-text-dark-primary">{operationLabel(run.operation)}</p>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            {run.cycle} · {run.mode === 'fixture' ? 'Test evidence' : 'Live official sources'} · {formatTime(run.startedAt)}
          </p>
        </div>
        <Chip label={run.status} variant={statusVariant(run.status)} size="sm" />
      </div>
    </summary>
    <div className="mt-3 border-t border-border-light pt-3 text-sm dark:border-border-dark">
      {run.errorSummary && <p className="mb-2 text-error-700 dark:text-error-300">{run.errorSummary}</p>}
      <dl className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {metrics.map(metric => (
          <div key={metric.key}><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{metric.label}</dt><dd>{displayValue(metric.value)}</dd></div>
        ))}
      </dl>
      {detail.isLoading && <p className="mt-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">Loading custody details…</p>}
      {detail.isError && <p className="mt-3 text-xs text-error-600 dark:text-error-300">Custody details could not be loaded.</p>}
      {detail.data && (
        <div className="mt-3 space-y-2 border-t border-border-light pt-3 dark:border-border-dark">
          <CustodyGroup label="Warnings" items={detail.data.warnings} />
          <details className="rounded-lg border border-border-light p-2 dark:border-border-dark">
            <summary className="min-h-11 cursor-pointer py-2 text-xs font-medium">Advanced custody details</summary>
            <div className="space-y-2 pt-2">
              <p className="break-all text-xs text-text-tertiary dark:text-text-dark-tertiary">Run ID: {run.id}</p>
              <CustodyGroup label="Snapshots" items={detail.data.snapshots} />
              <CustodyGroup label="Fetch attempts" items={detail.data.fetchAttempts} />
              <CustodyGroup label="Documents" items={detail.data.documents} />
              <CustodyGroup label="Job logs" items={detail.data.jobLogs} />
              {run.summary.checkpoint != null && <div className="rounded-lg bg-background-secondary p-2 text-xs dark:bg-background-dark-tertiary"><strong>Checkpoint:</strong> {displayValue(run.summary.checkpoint)}</div>}
            </div>
          </details>
        </div>
      )}
    </div>
  </details>
  );
};

const CustodyGroup: React.FC<{ label: string; items: unknown[] }> = ({ label, items }) => (
  <details className="rounded-lg bg-background-secondary p-2 dark:bg-background-dark-tertiary">
    <summary className="cursor-pointer text-xs font-medium">{label} ({items.length})</summary>
    {items.length === 0 ? <p className="mt-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">None retained.</p> : <ul className="mt-2 space-y-1 text-xs">{items.slice(0, 25).map((item, index) => <li key={index} className="break-words">{displayValue(item)}</li>)}</ul>}
  </details>
);

const ChangesView: React.FC = () => {
  const [source, setSource] = useState('');
  const [disposition, setDisposition] = useState('');
  const query = useInfiniteQuery(
    ['ballot-initiative-global-source-changes', source, disposition],
    ({ pageParam = undefined }) => fetchSourceChanges(pageParam, { source, disposition }),
    { getNextPageParam: page => page.nextCursor || undefined }
  );
  const changes = query.data?.pages.flatMap(page => page.entries) ?? [];
  return (
    <section className="card p-4" aria-labelledby="source-changes-heading" aria-busy={query.isFetching}>
      <h3 id="source-changes-heading" className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Source Changes</h3>
      <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">Append-only differences and whether they changed the initiative record.</p>
      <div className="mt-3 flex flex-wrap gap-2"><select className="input" value={source} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSource(event.target.value)} aria-label="Filter changes by source"><option value="">All sources</option><option value="colorado_sos_title_board">Secretary of State</option><option value="colorado_legislative_council_initiatives">Legislative Council</option></select><select className="input" value={disposition} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setDisposition(event.target.value)} aria-label="Filter changes by disposition"><option value="">All projection decisions</option><option value="applied">Applied</option><option value="sos_precedence_suppressed">SOS precedence</option><option value="human_owned_suppressed">Administrator managed</option><option value="observation_only">Observation only</option></select></div>
      {query.isLoading ? <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        : query.isError ? <ErrorState label="Source changes could not be loaded." retry={() => query.refetch()} />
          : changes.length === 0 ? <EmptyCopy icon={SuccessIcon} text="No material source changes have been recorded." />
            : <ol className="mt-4 divide-y divide-border-light dark:divide-border-dark">{changes.map(change => <ChangeRow key={change.id} change={change} />)}</ol>}
      {query.hasNextPage && <LoadMore loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()} />}
    </section>
  );
};

const ChangeRow: React.FC<{ change: SourceChangeEntry }> = ({ change }) => (
  <li className="py-4 first:pt-0">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="font-medium text-text-primary dark:text-text-dark-primary">{change.fieldPath.replace(/_/g, ' ')}</p>
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{sourceLabel(change.sourceCode)} · {change.changeType.replace(/_/g, ' ')}{change.operation ? ` · ${operationLabel(change.operation)}` : ''}{change.mode ? ` · ${change.mode}` : ''}</p>
      </div>
      <time className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{formatTime(change.occurredAt)}</time>
    </div>
    <p className="mt-2 break-words text-sm text-text-secondary dark:text-text-dark-secondary">{displayValue(change.beforeValue)} → {displayValue(change.afterValue)}</p>
    <p className="mt-2 text-xs font-medium text-text-secondary dark:text-text-dark-secondary">{dispositionLabel(change.disposition)}</p>
    {change.cause && <p className="mt-1 text-xs text-text-secondary dark:text-text-dark-secondary">Reason: {change.cause.replace(/_/g, ' ')}</p>}
    {change.sourceUrl && <a href={change.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline dark:text-primary-400">Exact source <OpenExternalIcon className="h-3 w-3" /></a>}
  </li>
);

const DocumentsView: React.FC<{ cycle: string; onAcquireOne: (initiative: DocumentCoverageEntry) => void; onBulkPreview: () => void }> = ({ cycle, onAcquireOne, onBulkPreview }) => {
  const [search, setSearch] = useState('');
  const query = useInfiniteQuery(
    ['ballot-initiative-document-coverage', cycle, search],
    ({ pageParam = undefined }) => fetchDocumentCoverage(cycle, search, pageParam),
    { getNextPageParam: page => page.nextCursor || undefined, enabled: Boolean(cycle) }
  );
  const entries = query.data?.pages.flatMap(page => page.entries) ?? [];
  return (
    <section className="card p-4" aria-labelledby="document-coverage-heading" aria-busy={query.isFetching}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h3 id="document-coverage-heading" className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Document Coverage</h3><p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">Selected official links and retained artifact availability.</p></div>
        <Button variant="outline" size="sm" onClick={onBulkPreview}>Preview Eligible Bulk Acquisition</Button>
      </div>
      <label className="mt-4 block"><span className="sr-only">Search document coverage</span><input type="search" value={search} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)} placeholder="Search by caption or initiative number" className="input w-full" /></label>
      {query.isLoading ? <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        : query.isError ? <ErrorState label="Document coverage could not be loaded." retry={() => query.refetch()} />
          : entries.length === 0 ? <EmptyCopy icon={FileText} text="No initiatives match this cycle and search." />
            : <div className="mt-4 space-y-3">{entries.map(entry => <CoverageCard key={entry.initiativeId} entry={entry} onAcquire={() => onAcquireOne(entry)} />)}</div>}
      {query.hasNextPage && <LoadMore loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()} />}
    </section>
  );
};

const CoverageCard: React.FC<{ entry: DocumentCoverageEntry; onAcquire: () => void }> = ({ entry, onAcquire }) => {
  const displayName = entry.popularName || entry.unofficialCaption || entry.ballotTitle || `Initiative #${entry.initiativeNumber}`;
  return (
    <article className="rounded-lg border border-border-light p-3 dark:border-border-dark">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h4 className="font-medium text-text-primary dark:text-text-dark-primary">{displayName}</h4><p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{entry.cycle} · Initiative #{entry.initiativeNumber}{entry.measureNumber ? ` · Ballot ${entry.measureNumber}` : ''}</p></div>
        <Button variant="outline" size="sm" onClick={onAcquire}>Acquire missing/revised documents</Button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
        {Object.entries(entry.documents).map(([type, state]) => (
          <div key={type} className="rounded-lg bg-background-secondary p-2 dark:bg-background-dark-tertiary" title={state.disabledReason ?? undefined}>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{documentTypeLabel(type)}</p>
            <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">{documentStateLabel(state.state, state.successfulArtifact)}</p>
            <details className="mt-1">
              <summary className="min-h-11 cursor-pointer py-2 text-xs text-primary-600 dark:text-primary-400">Technical details</summary>
              <p className="break-words text-xs text-text-tertiary dark:text-text-dark-tertiary">Type: {type} · State: {state.state}</p>
              {state.selectedCount > 1 && <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">Selected rounds: {state.selectedCount} · Retained artifacts: {state.successfulArtifactCount}</p>}
              {state.disabledReason && <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">{state.disabledReason}</p>}
            </details>
          </div>
        ))}
      </div>
    </article>
  );
};

const ErrorState: React.FC<{ label: string; retry: () => void }> = ({ label, retry }) => <div className="mt-4 flex items-center gap-3 rounded-lg border border-warning-300 p-3" role="alert"><WarningIcon className="h-5 w-5 text-warning-600" /><p className="flex-1 text-sm">{label}</p><Button variant="outline" size="sm" className="min-h-11" onClick={retry}>Retry</Button></div>;
const EmptyCopy: React.FC<{ icon: React.ElementType; text: string }> = ({ icon: Icon, text }) => <div className="flex flex-col items-center py-10 text-center"><Icon className="mb-2 h-7 w-7 text-text-tertiary dark:text-text-dark-tertiary" /><p className="text-sm text-text-secondary dark:text-text-dark-secondary">{text}</p></div>;
const LoadMore: React.FC<{ loading: boolean; onClick: () => void }> = ({ loading, onClick }) => <div className="mt-4 flex justify-center"><Button variant="secondary" size="sm" loading={loading} onClick={onClick}>Load more</Button></div>;

const BallotInitiativeOperations: React.FC<BallotInitiativeOperationsProps> = ({ activeView, cycle, onViewChange, onRefresh, onAcquireOne, onBulkPreview, actions, children, onNeedsPublish }) => {
  const activeLabel = useMemo(() => VIEW_OPTIONS.find(option => option.key === activeView)?.label, [activeView]);
  return (
    <div className="space-y-4">
      <SummaryPanel cycle={cycle} onRefresh={onRefresh} onNeedsPublish={onNeedsPublish} />
      <div className="flex flex-wrap items-center justify-between gap-3"><AdminTabs tabs={VIEW_OPTIONS} activeKey={activeView} onChange={key => onViewChange(key as OperationsView)} ariaLabel="Ballot initiative administration views" /><div className="flex items-center gap-2"><HelpTrigger contentKey="ballot-initiative-operations" size="md" ariaLabel="Help for ballot initiative operations" />{actions}</div></div>
      <h2 className="sr-only">{activeLabel}</h2>
      {activeView === 'initiatives' && children}
      {activeView === 'runs' && <RunsView />}
      {activeView === 'changes' && <ChangesView />}
      {activeView === 'documents' && <DocumentsView cycle={cycle} onAcquireOne={onAcquireOne} onBulkPreview={onBulkPreview} />}
    </div>
  );
};

export default BallotInitiativeOperations;
